const MAX_ENTRIES = 20;
const cache = new Map();
const keyOrder = [];
const LOCAL_STORAGE_PREFIX = "bvf-table-settings:";

export const buildBuildingViewFieldCacheKey = ({ userId, projectId, clientId }) =>
    [userId, projectId, clientId].map(v => String(v ?? "")).join("|");

const readPersistedTableSettings = cacheKey => {
    if (!cacheKey || cacheKey === "||") return null;
    try {
        const raw = localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${cacheKey}`);
        return raw ? JSON.parse(raw) : null;
    } catch (_e) {
        return null;
    }
};

const writePersistedTableSettings = (cacheKey, tableSettings) => {
    if (!cacheKey || cacheKey === "||") return;
    try {
        if (tableSettings == null) {
            localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}${cacheKey}`);
            return;
        }
        localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${cacheKey}`, JSON.stringify(tableSettings));
    } catch (_e) {
        // ignore quota / private mode errors
    }
};

export const mergeBuildingViewTableSettings = (primary, fallback) => {
    if (!primary) return fallback ?? null;
    if (!fallback?.config) return primary;
    const mergedConfig = { ...(primary.config || {}) };
    Object.entries(fallback.config).forEach(([key, entry]) => {
        if (!entry || typeof entry !== "object") return;
        if (entry.isVisible === false || entry.isVisible === "false" || entry.isVisible === 0 || entry.isVisible === "0") {
            mergedConfig[key] = { ...(mergedConfig[key] || {}), ...entry, isVisible: false };
        }
    });
    const keys = Array.isArray(primary.keys)
        ? [...new Set([...primary.keys, ...(Array.isArray(fallback.keys) ? fallback.keys : [])])]
        : fallback.keys;
    return { ...primary, keys, config: mergedConfig };
};

export const getCachedBuildingViewTableSettings = cacheKey => {
    if (!cacheKey || !cache.has(cacheKey)) return undefined;
    return cache.get(cacheKey);
};

export const setCachedBuildingViewTableSettings = (cacheKey, tableSettings) => {
    if (!cacheKey) return;
    if (!cache.has(cacheKey)) {
        keyOrder.push(cacheKey);
        if (keyOrder.length > MAX_ENTRIES) {
            const evict = keyOrder.shift();
            cache.delete(evict);
            writePersistedTableSettings(evict, null);
        }
    }
    cache.set(cacheKey, tableSettings);
    writePersistedTableSettings(cacheKey, tableSettings);
};

export const getPersistedBuildingViewTableSettings = cacheKey => readPersistedTableSettings(cacheKey);

export const invalidateBuildingViewFieldCache = ({ userId, projectId, clientId }) => {
    const cacheKey = buildBuildingViewFieldCacheKey({ userId, projectId, clientId });
    if (!cacheKey || cacheKey === "||") return;
    cache.delete(cacheKey);
    writePersistedTableSettings(cacheKey, null);
    const idx = keyOrder.indexOf(cacheKey);
    if (idx >= 0) keyOrder.splice(idx, 1);
};

export const hasCachedBuildingViewTableSettings = cacheKey =>
    cacheKey != null && cache.has(cacheKey);

export const hasStoredBuildingViewTableSettings = cacheKey =>
    hasCachedBuildingViewTableSettings(cacheKey) || !!getPersistedBuildingViewTableSettings(cacheKey);
